import React, { useEffect, useState } from 'react';
import { Table, InputNumber, Popconfirm, Form, Button, Select } from 'antd';
import { isEmpty } from 'lodash';
import { ALTO_TRANSACTION_TYPES } from '../pages/CreatePayout';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const { Option } = Select;

export function getBankAccountInfo(items = [], id) {
  if (isEmpty(items)) return null;

  return items.filter((i) => i.account_name === id)[0];
}

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  let inputNode =
    inputType === 'number' ? (
      <InputNumber />
    ) : (
      <Select size="small">
        {ALTO_TRANSACTION_TYPES.map((item) => (
          <Option key={item} value={item}>
            {item}
          </Option>
        ))}
      </Select>
    );

  if (title === 'Account') {
    inputNode = (
      <Select size="small">
        {record.altoBankAccounts.map((item) => {
          const bankAccount = getBankAccountInfo(
            record.bankAccountsAlto,
            item.backAccountName
          );

          return (
            <Option key={item.backAccountName} value={item.backAccountName}>
              {`${bankAccount.bankAccountNameLinked} (${bankAccount.account_number})`}
            </Option>
          );
        })}
      </Select>
    );
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export const EditableAltoTable = ({
  originData = [],
  localColumns = [],
  title = '',
  loading = false,
  onSubmit = () => Promise.resolve({}),
  pushToPayout = () => {},
  payoutId = '',
  amountToSend = '',
  isSelectable = false,
}) => {
  const [form] = Form.useForm();
  const [data, setData] = useState(originData);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editingKey, setEditingKey] = useState('');

  const isEditing = (record) => record.key === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      ...record,
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...data];
      const index = newData.findIndex((item) => key === item.key);

      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, { ...item, ...row });

        setData(newData);
        setEditingKey('');
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const columns = [
    ...localColumns,
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Button
              htmlType="submit"
              type="primary"
              onClick={() => save(record.key)}
              style={{
                marginRight: 8,
              }}
            >
              Save
            </Button>
            <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
              <Button danger>Cancel</Button>
            </Popconfirm>
          </span>
        ) : (
          <Button
            htmlType="button"
            disabled={editingKey !== ''}
            onClick={() => edit(record)}
          >
            Edit
          </Button>
        );
      },
    },
  ];
  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType:
          col.dataIndex === 'note' || col.dataIndex === 'account'
            ? 'text'
            : 'number',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const onSelectChange = (selected = []) => {
    setSelectedRowKeys(selected);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <Form form={form} component={false}>
      {isSelectable ? (
        <Table
          title={() => <h3>{title}</h3>}
          size="small"
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={{
            onChange: cancel,
            total: originData.length,
            position: ['bottomCenter'],
            hideOnSinglePage: true,
            pageSize: 20,
          }}
          rowKey="_id"
          rowSelection={rowSelection}
        />
      ) : (
        <Table
          title={() => <h3>{title}</h3>}
          size="small"
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={{
            onChange: cancel,
            total: originData.length,
            position: ['bottomCenter'],
            hideOnSinglePage: true,
            pageSize: 20,
          }}
          rowKey="_id"
        />
      )}

      <div style={{ height: 20 }}></div>

      <Button
        size="large"
        htmlType="button"
        type="primary"
        loading={loading}
        onClick={async () => {
          MySwal.fire({
            title: 'Please confirm payout action',
            icon: 'warning',
            confirmButtonText: 'Send',
            confirmButtonColor: '#e74c3c',
            showCancelButton: true,
          }).then(async (result) => {
            if (result.isConfirmed) {
              if (isSelectable) {
                const inputData = data.filter((i) =>
                  selectedRowKeys.includes(i._id)
                );
                await onSubmit(inputData);
              } else {
                await onSubmit(data);
              }
            }
          });
        }}
        disabled={loading}
      >
        Send
      </Button>
      {payoutId && (
        <Button
          style={{ marginLeft: 10 }}
          size="large"
          htmlType="button"
          loading={loading}
          onClick={pushToPayout}
        >
          Go to payout summary
        </Button>
      )}
    </Form>
  );
};
